import { graphqlRequest } from "@/graphql/client";
import { putMessage, deleteMessages, markUnifiedMessage } from "@/graphql/queries";
import { searchContactsPage } from "@/utils/contactsFetcher";

/**
 * Loads one contact by email for the conversation viewer panel.
 * Delegates to `searchContactsPage` so GraphQL variables match the Contacts dashboard
 * (`limit` 50, `next_token: null`, sanitized `email`). A direct `searchContact` with
 * `limit: 1` has been observed to hit `Lambda:ExecutionTimeoutException` while the
 * Contacts-shaped request succeeds for the same customer/email.
 *
 * @param {{ customer: string, email: string }} params
 * @returns {Promise<Record<string, unknown>|null>}
 */
export async function searchContactByEmail({ customer, email }) {
  const emailArg = typeof email === "string" ? email.trim() : email;
  const { contacts } = await searchContactsPage(customer, { email: emailArg });
  return contacts[0] ?? null;
}

/**
 * @param {{ customer: string, campaignId: string, thread: string, timestamp: string }} params
 * @returns {Promise<void>}
 */
export async function markUnifiedMessageRead({ customer, campaignId, thread, timestamp }) {
  const markResult = await graphqlRequest({
    query: markUnifiedMessage,
    variables: {
      customer,
      campaign: campaignId,
      thread,
      timestamp,
      read: true,
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const node = markResult?.data?.markUnifiedMessage;
  const body = node?.body ? JSON.parse(node.body) : null;
  if (typeof node?.statusCode === "number" && node.statusCode >= 400) {
    throw new Error(`markUnifiedMessage failed (${node.statusCode})`);
  }
  if (body && typeof body === "object" && body.success === false) {
    throw new Error(body.message || "UnifiedMessage not found or not updated");
  }
}

/**
 * @param {{
 *   message_id: string,
 *   campaign_id: string,
 *   body: string,
 *   check_gramma: boolean,
 *   new_reply: boolean,
 * }} variables
 * @returns {Promise<{ body?: string, statusCode?: string }|null|undefined>}
 */
export async function putMessageRequest(variables) {
  const response = await graphqlRequest({
    query: putMessage,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });
  return response.data?.putMessage;
}

/**
 * @param {Record<string, unknown>} messagePayload
 * @returns {Promise<void>}
 */
export async function deleteMessagesRequest(messagePayload) {
  await graphqlRequest({
    query: deleteMessages,
    variables: { messages: [JSON.stringify(messagePayload)] },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });
}
